import { useMemo } from 'react';
import { Stack } from '@etg/wings';
import { AgeType } from '@eti/schema-types';
import { useTranslation } from '@eti/providers';
import BaggageItem from './BaggageItem';
import type { TravelerBaggageInfo } from './models';

interface BoundBaggageInfoPerTravelerProps {
  className?: string;
  dataTestId?: string;
  travelerBaggageInfo: TravelerBaggageInfo;
}

const BoundBaggageInfoPerTraveler = ({
  className,
  dataTestId,
  travelerBaggageInfo,
}: BoundBaggageInfoPerTravelerProps) => {
  const { t } = useTranslation();

  const shouldNotShowBaggageList = useMemo(
    () =>
      travelerBaggageInfo.ageType === AgeType.Inf &&
      !travelerBaggageInfo.personalItem &&
      !travelerBaggageInfo.cabinBaggage &&
      !travelerBaggageInfo.checkedBaggage &&
      !travelerBaggageInfo.secondBaggage,
    [travelerBaggageInfo],
  );

  if (shouldNotShowBaggageList) {
    return (
      <div data-testid={`${dataTestId}-infant-info`}>
        {t('OrderDetails.Baggage.Drawer.Infant.Description')}
      </div>
    );
  }

  return (
    <Stack align="start" className={className} key={travelerBaggageInfo.id} role="list" spacing={0}>
      {travelerBaggageInfo.personalItem && (
        <BaggageItem
          baggageItem={travelerBaggageInfo.personalItem}
          baggageTypeText={t('OrderDetails.Baggage.Analysis.PersonalItem.Header')}
          dataTestId={`${dataTestId}-personal-item`}
        />
      )}
      {travelerBaggageInfo.cabinBaggage && (
        <BaggageItem
          baggageItem={travelerBaggageInfo.cabinBaggage}
          baggageTypeText={t('OrderDetails.Baggage.Analysis.HandBaggage.Header')}
          dataTestId={`${dataTestId}-cabin-baggage`}
        />
      )}
      {travelerBaggageInfo.checkedBaggage && (
        <BaggageItem
          baggageItem={travelerBaggageInfo.checkedBaggage}
          baggageTypeText={t('OrderDetails.Baggage.Analysis.CheckedBaggage.Header')}
          dataTestId={`${dataTestId}-checked-baggage`}
        />
      )}
      {travelerBaggageInfo.secondBaggage && (
        <BaggageItem
          baggageItem={travelerBaggageInfo.secondBaggage}
          baggageTypeText={t('OrderDetails.Baggage.Analysis.SecondBaggage.Header')}
          dataTestId={`${dataTestId}-second-baggage`}
        />
      )}
    </Stack>
  );
};

export default BoundBaggageInfoPerTraveler;
